/* eslint-disable max-lines, class-methods-use-this, max-lines-per-function */
import { api } from "jiffy-api";
import { registerMixpanel, track } from "jiffy-analytics";
import { EventBus, Events } from "jiffy-store";
import { getOrderNumber } from "common/utils/user-context";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import AddressListRenderer from "./address-list-renderer";

const MIXPANEL_EVENTS = {
  TYPEAHEAD_DISPLAYED: "address_typeahead_displayed",
  EDIT_ADDRESS_CLICKED: "edit_address_clicked",
  ADDRESS_SELECTED: "address_selected"
};

const SELECTORS = {
  CONTAINER: ".js-local-delivery-address-list",
  ITEM: ".js-local-delivery-address-list-item",
  EDIT_BTN: ".js-local-delivery-address-list-edit-btn",
  SLIDE_PANEL: ".js-slide-panel[data-panel-id='local-delivery-slide-panel']"
};

const CSS_CLASSES = {
  LOADING: "local-delivery-address-list--loading",
  SELECTED: "local-delivery-address-list__item--selected"
};

/**
 * @class AddressList
 *
 * Manages the address list for local delivery.
 * Fetches addresses from API and renders them dynamically.
 * Supports both logged-in users and guest users.
 *
 * @example
 * const addressList = new AddressList();
 * addressList.init();
 */
class AddressList {
  static #ERRORS = {
    CONTAINER_NOT_FOUND: "Address list container not found",
    FETCH_ADDRESSES_FAILED: "Address list failed to fetch addresses",
    NO_ORDER_NUMBER: "Address list: No order number found for guest user",
    SET_SHIPPING_ADDRESS_FAILED: "Address list failed to set shipping address",
    HONEYBADGER_MESSAGE: "AddressList: Failed to set shipping address"
  };

  #addresses = [];

  #selectedAddressId = null;

  #isLoading = false;

  /**
   * Gets the current list of addresses
   * @public
   * @returns {Array<Object>} The addresses array
   */
  get addresses() {
    return this.#addresses;
  }

  /**
   * Gets the address list container element
   * @private
   * @returns {HTMLElement|null} The address list container
   */
  get #$addressList() {
    return document.querySelector(SELECTORS.CONTAINER);
  }

  /**
   * Gets the address items
   * @private
   * @returns {NodeList} The address items
   */
  get #$addressItems() {
    return this.#$addressList?.querySelectorAll(SELECTORS.ITEM);
  }

  /**
   * Gets the slide panel element
   * @private
   * @returns {HTMLElement|null} The slide panel element
   */
  get #$slidePanel() {
    return document.querySelector(SELECTORS.SLIDE_PANEL);
  }

  /**
   * Initializes the address list component
   * @public
   */
  async init() {
    if (!this.#$addressList) {
      // eslint-disable-next-line no-console
      console.warn(AddressList.#ERRORS.CONTAINER_NOT_FOUND);
      return;
    }

    await this.#fetchAndRenderAddresses();
  }

  /**
   * Fetches addresses and renders them in the list
   * @private
   */
  async #fetchAndRenderAddresses() {
    this.#setLoading(true);

    try {
      const response = await this.#fetchAddresses();
      const addresses = response?.addresses || [];
      const activeAddressId = response?.active_address_id;

      if (addresses && addresses.length > 0) {
        // Transform addresses to camelCase for consistent property access
        this.#addresses = addresses.map(addr => objNamesToCamelCase(addr));
        this.#selectedAddressId = activeAddressId || addresses[0].id;
        this.#renderAddresses();
        this.#trackTypeaheadDisplayed();
      } else {
        this.#renderEmptyState();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(AddressList.#ERRORS.FETCH_ADDRESSES_FAILED, error);
      this.#renderErrorState();
    } finally {
      this.#setLoading(false);
    }
  }

  /**
   * Fetches addresses based on user login status
   * @private
   * @returns {Promise<Object>} Response object with addresses and active_address_id
   */
  async #fetchAddresses() {
    const orderNumber = getOrderNumber();

    if (!orderNumber) {
      // eslint-disable-next-line no-console
      console.warn(AddressList.#ERRORS.NO_ORDER_NUMBER);
      return { addresses: [], active_address_id: null };
    }

    // eslint-disable-next-line no-useless-catch
    try {
      const response = await api.account.addresses.getAddress({
        order_number: orderNumber,
        local_delivery_address: true
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Renders the list of addresses in the UI
   * @private
   */
  #renderAddresses() {
    if (!this.#$addressList) return;

    const addressesHtml = this.#addresses
      .map(address => {
        const isSelected = address.id === this.#selectedAddressId;
        return AddressListRenderer.createAddressItemHtml(address, isSelected);
      })
      .join("");

    this.#$addressList.innerHTML = addressesHtml;
    this.#attachEventListeners();
  }

  /**
   * Tracks when address list is displayed to existing users
   * @private
   */
  #trackTypeaheadDisplayed() {
    const verifiedCount = this.#addresses.filter(addr => addr.verifiedAt).length;
    const unverifiedCount = this.#addresses.length - verifiedCount;

    track(MIXPANEL_EVENTS.TYPEAHEAD_DISPLAYED, {
      saved_address_count: this.#addresses.length,
      verified_address_count: verifiedCount,
      unverified_address_count: unverifiedCount
    });
  }

  /**
   * Renders empty state when no addresses are found
   * @private
   */
  #renderEmptyState() {
    if (!this.#$addressList) return;
    this.#$addressList.innerHTML = AddressListRenderer.renderEmptyState();
  }

  /**
   * Renders error state when address fetching fails
   * @private
   */
  #renderErrorState() {
    if (!this.#$addressList) return;
    this.#$addressList.innerHTML = AddressListRenderer.renderErrorState();
  }

  /**
   * Attaches click event listeners to address items
   * @private
   */
  #attachEventListeners() {
    this.#$addressItems?.forEach($item => {
      $item.addEventListener("click", event => {
        if (event.target.closest(SELECTORS.EDIT_BTN)) {
          event.stopPropagation();
          this.#handleEditAddress(Number($item.dataset.addressId));
        } else {
          this.#handleAddressSelection(Number($item.dataset.addressId));
        }
      });
    });
  }

  /**
   * Handles edit button click for an address
   * Opens the address editor slide panel with the selected address data
   * @private
   * @param {number} addressId - The ID of the address to edit
   */
  #handleEditAddress(addressId) {
    const address = this.#getSelectedAddress(addressId);

    if (!address) {
      // eslint-disable-next-line no-console
      console.error("AddressList: Address not found for editing", addressId);
      return;
    }

    // Track edit button click
    this.#trackEditAddressClicked(address);

    // Emit event to open the address editor with the address data
    EventBus.publish(Events.SHOW_ADDRESS_EDITOR_PANEL, address);
  }

  /**
   * Tracks when user clicks edit button on an address
   * @param {Object} address - The address being edited
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  #trackEditAddressClicked(address) {
    track(MIXPANEL_EVENTS.EDIT_ADDRESS_CLICKED, {
      address_id: String(address.id),
      zipcode: address.zipcode || "",
      verification_status: address.verifiedAt ? "verified" : "unverified"
    });
  }

  /**
   * Gets an address from the addresses array by ID
   * @private
   * @param {number} addressId - The ID of the address to find
   * @returns {Object|null} The address object or null if not found
   */
  #getSelectedAddress(addressId) {
    return this.#addresses.find(({ id }) => id === addressId) || null;
  }

  /**
   * Handles successful address selection - updates header and analytics
   * @private
   * @param {number} addressId - The ID of the selected address
   */
  #handleAddressSelectionSuccess(addressId) {
    const selectedAddress = this.#getSelectedAddress(addressId);

    if (selectedAddress) {
      // Register Mixpanel with zipcode
      if (selectedAddress.zipcode) {
        registerMixpanel({ zipcode: selectedAddress.zipcode });
      }

      // reload the page
      if (this.#$slidePanel?.dataset.pageReload === "true") {
        window.location.reload();
      } else {
        this.#$slidePanel?.close();
        EventBus.publish(Events.LOCAL_DELIVERY_ADDRESS_UPDATED, selectedAddress);
      }
    }
  }

  /**
   * Handles address selection error - rollback and logging
   * @private
   * @param {number} addressId - The ID that failed to be selected
   * @param {number} previousAddressId - The previous address ID to rollback to
   * @param {Error} error - The error that occurred
   */
  #handleAddressSelectionError(addressId, previousAddressId, error) {
    // Rollback to previous selection
    this.#selectedAddressId = previousAddressId;
    this.#updateSelectedState();

    // eslint-disable-next-line no-console
    console.error(AddressList.#ERRORS.SET_SHIPPING_ADDRESS_FAILED, error);

    // Report to Honeybadger if available
    window.Honeybadger?.notify?.(AddressList.#ERRORS.HONEYBADGER_MESSAGE, {
      params: {
        addressId,
        previousAddressId,
        error: error.message || String(error)
      }
    });
  }

  /**
   * Handles address selection when user clicks on an address
   * @private
   * @param {number} addressId - The ID of the selected address
   */
  async #handleAddressSelection(addressId) {
    if (this.#selectedAddressId === addressId) return;

    const previousAddressId = this.#selectedAddressId;

    // Track address selection before API call
    this.#trackAddressSelected(addressId);

    // Update UI optimistically
    this.#selectedAddressId = addressId;
    this.#updateSelectedState();
    this.#setLoading(true);

    try {
      await api.account.addresses.setOne(
        { order_number: getOrderNumber() },
        { ship_address_id: addressId }
      );
      this.#handleAddressSelectionSuccess(addressId);
    } catch (error) {
      this.#handleAddressSelectionError(addressId, previousAddressId, error);
      this.#setLoading(false);
    }
  }

  /**
   * Tracks when user selects an address from the list
   * @param {number} addressId - The ID of the selected address
   * @private
   */
  #trackAddressSelected(addressId) {
    const address = this.#getSelectedAddress(addressId);
    if (!address) return;

    // Find position in the list (1-indexed)
    const selectionPosition = this.#addresses.findIndex(({ id }) => id === addressId) + 1;

    const trackingData = {
      selection_source: address.verifiedAt ? "saved_verified" : "saved_unverified",
      selection_position: selectionPosition || 1,
      zipcode: address.zipcode || "",
      address_verification_status: address.verifiedAt ? "verified" : "unverified",
      requires_2fa: false,
      component_type: "modal",
      address_id: String(address.id)
    };

    track(MIXPANEL_EVENTS.ADDRESS_SELECTED, trackingData);
  }

  /**
   * Selects an address by ID (public API)
   * @public
   * @param {number} addressId - The ID of the address to select
   */
  async selectAddress(addressId) {
    await this.#handleAddressSelection(addressId);
  }

  /**
   * Updates the UI to reflect the selected address state
   * @private
   */
  #updateSelectedState() {
    this.#$addressItems?.forEach($item => {
      const addressId = parseInt($item.dataset.addressId, 10);
      const isSelected = addressId === this.#selectedAddressId;

      $item.classList.toggle(CSS_CLASSES.SELECTED, isSelected);
    });
  }

  /**
   * Sets the loading state and updates UI accordingly
   * @private
   * @param {boolean} isLoading - Loading state to set
   */
  #setLoading(isLoading) {
    this.#isLoading = isLoading;

    this.#$addressList?.classList?.toggle(CSS_CLASSES.LOADING, isLoading);
  }
}

export default AddressList;
